import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { apiGet } from '../services/apiService';
import { ENDPOINTS } from '../config/api';
import StatusPill from '../components/ui/StatusPill';

const n = v => Number(v) || 0;
const fmtMoney = (v) => { try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n(v)); } catch { return `$${n(v).toLocaleString()}`; } };
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';
const toArr = x => Array.isArray(x) ? x : (x?.items || []);

const Stat = ({ label, value, sub, tone, first }) => (
  <div style={{ paddingRight: 26, paddingLeft: first ? 0 : 26, borderLeft: first ? 'none' : '1px solid #26262B' }}>
    <div className="field-label" style={{ marginBottom: 6 }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 700, color: tone || '#FAFAFA', fontFamily: '"JetBrains Mono", monospace', lineHeight: 1 }}>{value}</div>
    {sub && <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#A1A1AA', marginTop: 5 }}>{sub}</div>}
  </div>
);

export default function BillingPage() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { setSubs(toArr(await apiGet(ENDPOINTS.ADMIN.SUBSCRIPTIONS, { limit: 500 }))); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => {
    const active = subs.filter(s => (s.status || 'active') === 'active');
    return {
      mrr: active.reduce((a, s) => a + n(s.mrr ?? s.amount), 0),
      activeCount: active.length,
      trialing: subs.filter(s => s.status === 'trialing').length,
      pastDue: subs.filter(s => s.status === 'past_due').length,
    };
  }, [subs]);

  return (
    <div className="page">
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#A1A1AA', marginBottom: 4 }}>Console / <strong style={{ color: '#FAFAFA' }}>Billing</strong></div>
        <h1 style={{ fontFamily: '"Geist","Inter",system-ui,sans-serif', fontWeight: 600, fontSize: 44, letterSpacing: '-0.02em', color: '#FAFAFA', margin: 0, lineHeight: 1.05 }}>
          <em style={{ fontStyle: 'italic', background: 'linear-gradient(135deg,#60A5FA,#3B82F6 55%,#2563EB)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Subscriptions</em>
        </h1>
        <p style={{ color: '#A1A1AA', fontSize: 14, margin: '4px 0 0' }}>Plans and revenue across organizations.</p>
      </div>

      {error && <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,.13)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 8, color: '#EF4444', fontSize: 13, marginBottom: 14 }}>{error}</div>}

      <div className="flex flex-wrap items-start" style={{ gap: 0, rowGap: 18, paddingBottom: 20, borderBottom: '1px solid #26262B', marginBottom: 22 }}>
        <Stat first label="MRR" value={fmtMoney(totals.mrr)} tone="#22C55E" />
        <Stat label="Active" value={totals.activeCount} />
        <Stat label="Trialing" value={totals.trialing} tone="#F59E0B" />
        <Stat label="Past due" value={totals.pastDue} tone={totals.pastDue > 0 ? '#EF4444' : undefined} />
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="tbl" style={{ width: '100%' }}>
          <thead><tr><th>Organization</th><th>Plan</th><th>Seats</th><th style={{ textAlign: 'right' }}>MRR</th><th>Status</th><th>Renews</th></tr></thead>
          <tbody>
            {loading && <tr><td colSpan={6} style={{ textAlign: 'center', padding: '40px', color: '#A1A1AA' }}>Loading…</td></tr>}
            {!loading && subs.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', padding: '40px', color: '#71717A' }}>No subscriptions</td></tr>}
            {subs.map(s => (
              <tr key={s.id || s.organization_id}>
                <td style={{ color: '#FAFAFA' }}>{s.organization_name || s.org_name || '-'}</td>
                <td style={{ color: '#D4D4D8' }}>{s.plan || s.plan_name || '-'}</td>
                <td className="tbl-mono">{s.seats ?? '-'}</td>
                <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 600, color: '#FAFAFA' }}>{fmtMoney(s.mrr ?? s.amount)}</td>
                <td><StatusPill status={(s.status || 'active') === 'active' ? 'active' : (s.status === 'past_due' ? 'overdue' : 'on_hold')} label={(s.status || 'active').replace('_', ' ').replace(/^\w/, c => c.toUpperCase())} /></td>
                <td className="tbl-mono">{fmtDate(s.current_period_end || s.renews_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
